/**
 * Device Data Flow Visualizer
 * Shows data flow from physical device to dashboard using ReactFlow
 */

import { useMemo } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  ConnectionLineType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import type { Device } from '../types';

interface DeviceDataFlowVisualizerProps {
  device: Device;
}

const nodeWidth = 200;
const nodeHeight = 80;

// Auto-layout using dagre
function getLayoutedElements(nodes: Node[], edges: Edge[]) {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'TB', ranksep: 80, nodesep: 60 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

export default function DeviceDataFlowVisualizer({ device }: DeviceDataFlowVisualizerProps) {
  const { nodes, edges } = useMemo(() => {
    const rawNodes: Node[] = [
      // 1. Physical Device
      {
        id: 'physical-device',
        type: 'default',
        data: {
          label: (
            <div className="text-center p-2">
              <div className="font-semibold text-sm">Physical Device</div>
              <div className="text-xs text-gray-600">{device.deviceId}</div>
              <div className="text-xs text-gray-500">{device.metadata?.manufacturer} {device.metadata?.model}</div>
            </div>
          ),
        },
        position: { x: 0, y: 0 },
        style: {
          background: '#dbeafe',
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          width: nodeWidth,
        },
      },

      // 2. Listener
      {
        id: 'listener',
        type: 'default',
        data: {
          label: (
            <div className="text-center p-2">
              <div className="font-semibold text-sm">Listener</div>
              <div className="text-xs text-gray-600">TCP/UDP/HTTP</div>
              <div className="text-xs text-gray-500">Receives raw data</div>
            </div>
          ),
        },
        position: { x: 0, y: 0 },
        style: {
          background: '#dcfce7',
          border: '2px solid #22c55e',
          borderRadius: '8px',
          width: nodeWidth,
        },
      },

      // 3. Decoder
      {
        id: 'decoder',
        type: 'default',
        data: {
          label: (
            <div className="text-center p-2">
              <div className="font-semibold text-sm">Decoder</div>
              <div className="text-xs text-gray-600">{device.decoderId || 'N/A'}</div>
              <div className="text-xs text-gray-500">Parses payload</div>
            </div>
          ),
        },
        position: { x: 0, y: 0 },
        style: {
          background: '#fef3c7',
          border: '2px solid #f59e0b',
          borderRadius: '8px',
          width: nodeWidth,
        },
      },

      // 4. Pulsar
      {
        id: 'pulsar',
        type: 'default',
        data: {
          label: (
            <div className="text-center p-2">
              <div className="font-semibold text-sm">Pulsar Topic</div>
              <div className="text-xs text-gray-600">iot.raw.{device.tenant}</div>
              <div className="text-xs text-gray-500">Message broker</div>
            </div>
          ),
        },
        position: { x: 0, y: 0 },
        style: {
          background: '#e9d5ff',
          border: '2px solid #a855f7',
          borderRadius: '8px',
          width: nodeWidth,
        },
      },

      // 5. Interpreter
      {
        id: 'interpreter',
        type: 'default',
        data: {
          label: (
            <div className="text-center p-2">
              <div className="font-semibold text-sm">Interpreter (A7)</div>
              <div className="text-xs text-gray-600">CloudEvent → Telemetry</div>
              <div className="text-xs text-gray-500">Data transformation</div>
            </div>
          ),
        },
        position: { x: 0, y: 0 },
        style: {
          background: '#fecaca',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          width: nodeWidth,
        },
      },

      // 6. TimescaleDB
      {
        id: 'timescaledb',
        type: 'default',
        data: {
          label: (
            <div className="text-center p-2">
              <div className="font-semibold text-sm">TimescaleDB</div>
              <div className="text-xs text-gray-600">Time-series storage</div>
              <div className="text-xs text-gray-500">Persistent data</div>
            </div>
          ),
        },
        position: { x: 0, y: 0 },
        style: {
          background: '#d1fae5',
          border: '2px solid #10b981',
          borderRadius: '8px',
          width: nodeWidth,
        },
      },

      // 7. Dashboard/API
      {
        id: 'dashboard',
        type: 'default',
        data: {
          label: (
            <div className="text-center p-2">
              <div className="font-semibold text-sm">Dashboard / API</div>
              <div className="text-xs text-gray-600">Farmer Portal</div>
              <div className="text-xs text-gray-500">Data visualization</div>
            </div>
          ),
        },
        position: { x: 0, y: 0 },
        style: {
          background: '#ddd6fe',
          border: '2px solid #8b5cf6',
          borderRadius: '8px',
          width: nodeWidth,
        },
      },
    ];

    // Optional: PIM Product node (if device has productId)
    if (device.productId) {
      rawNodes.splice(1, 0, {
        id: 'pim-product',
        type: 'default',
        data: {
          label: (
            <div className="text-center p-2">
              <div className="font-semibold text-sm">PIM Product</div>
              <div className="text-xs text-gray-600">{device.sku || 'Product'}</div>
              <div className="text-xs text-gray-500">Product metadata</div>
            </div>
          ),
        },
        position: { x: 250, y: 0 },
        style: {
          background: '#fbcfe8',
          border: '2px solid #ec4899',
          borderRadius: '8px',
          width: nodeWidth,
        },
      });
    }

    const rawEdges: Edge[] = [
      {
        id: 'e1',
        source: 'physical-device',
        target: 'listener',
        type: ConnectionLineType.SmoothStep,
        animated: true,
        label: 'Raw data',
        style: { stroke: '#3b82f6', strokeWidth: 2 },
      },
      {
        id: 'e2',
        source: 'listener',
        target: 'decoder',
        type: ConnectionLineType.SmoothStep,
        animated: true,
        label: 'Hex/JSON',
        style: { stroke: '#22c55e', strokeWidth: 2 },
      },
      {
        id: 'e3',
        source: 'decoder',
        target: 'pulsar',
        type: ConnectionLineType.SmoothStep,
        animated: true,
        label: 'CloudEvent',
        style: { stroke: '#f59e0b', strokeWidth: 2 },
      },
      {
        id: 'e4',
        source: 'pulsar',
        target: 'interpreter',
        type: ConnectionLineType.SmoothStep,
        animated: true,
        label: 'Subscribe',
        style: { stroke: '#a855f7', strokeWidth: 2 },
      },
      {
        id: 'e5',
        source: 'interpreter',
        target: 'timescaledb',
        type: ConnectionLineType.SmoothStep,
        animated: true,
        label: 'Insert',
        style: { stroke: '#ef4444', strokeWidth: 2 },
      },
      {
        id: 'e6',
        source: 'timescaledb',
        target: 'dashboard',
        type: ConnectionLineType.SmoothStep,
        animated: true,
        label: 'Query',
        style: { stroke: '#10b981', strokeWidth: 2 },
      },
    ];

    // Optional: Link to PIM product (metadata link)
    if (device.productId) {
      rawEdges.push({
        id: 'e-pim',
        source: 'physical-device',
        target: 'pim-product',
        type: ConnectionLineType.Step,
        label: 'instance_of',
        style: { stroke: '#ec4899', strokeWidth: 2, strokeDasharray: '5 5' },
        animated: false,
      });
    }

    return getLayoutedElements(rawNodes, rawEdges);
  }, [device]);

  return (
    <div style={{ height: '600px', width: '100%' }} className="border border-gray-200 rounded-lg">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        attributionPosition="bottom-left"
        minZoom={0.5}
        maxZoom={1.5}
        defaultEdgeOptions={{
          type: ConnectionLineType.SmoothStep,
          animated: true,
        }}
      >
        <Background color="#f3f4f6" gap={16} />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            const style = node.style as any;
            return style?.background || '#e5e7eb';
          }}
          maskColor="rgba(0, 0, 0, 0.1)"
        />
      </ReactFlow>
    </div>
  );
}
